<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <simple-item-form
    v-else
    ref="itemForm"
    :initItem="item"
    :submitAction="update"
    :successMsg="$t('actions.saveChangesSuccess')"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'

//components
import SimpleItemForm from './SimpleItemForm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'

// models
import Item from '@/models/inventory/Item'

// Context
import Brand from '@/models/inventory/Brand'
import Category from '@/models/inventory/Category'
import Vendor from '@/models/vendor/Vendor'
import Tag from '@/models/inventory/Tag'
import Stock from '@/models/inventory/Stock'

interface Option {
  name: string
  values: string[]
}

export default defineComponent({
  components: { SimpleItemForm, LoadingSpinner, AlertBadge },
  name: 'simple-item',

  data() {
    return {
      loading: true,
      error: { title: '', body: '' },

      item: new Item()
    }
  },

  methods: {
    // update gets called by ItemForm on form submit
    async update(data: any) {
      try {
        if (data.isSerialized && data.unlimitedStock) {
          this.error.title = this.$t("errors.client.itemSerializedAndUnlimitedError")
          this.error.body = ""
          return this.$t("errors.client.itemSerializedAndUnlimitedError");
        }

        const url = `${Item.ENDPOINT}/${this.$route.params.id}`
        const resp = await this.$http.put<any>(url, data)
      } catch (error) {
        return error
      }
    }
  },

  async beforeMount() {
    // fetch the item by its id
    try {
      const url = `${Item.ENDPOINT}/${this.$route.params.id}`
      const resp = await this.$http.get<Item>(url)
      const nilUUID = '00000000-0000-0000-0000-000000000000'
      // replace any undefined value
      if (!resp.itemBrand || resp.itemBrand.id === nilUUID) resp.itemBrand = new Brand()
      if (!resp.itemCategory || resp.itemCategory.id === nilUUID) resp.itemCategory = new Category()
      if (!resp.vendor || resp.vendor.id === nilUUID) resp.vendor = new Vendor()
      if (!resp.itemTags) resp.itemTags = new Array<Tag>()
      if (!resp.itemStocks) resp.itemStocks = new Array<Stock>()

      this.item = Item.from(resp)
    } catch (errorResp) {
      this.error.title = errorResp.title
      this.error.body = errorResp.body
    }

    this.loading = false
  },

  // Navigation guard to check child component's dirty state
  beforeRouteLeave(to: any, from: any, next: any) {
    const itemForm = this.$refs.itemForm as any
    if (itemForm && itemForm.formIsDirty) {
      const answer = window.confirm(
        this.$ctx.locale === 'ar'
          ? 'لديك تغييرات غير محفوظة. هل تريد المغادرة؟'
          : 'You have unsaved changes. Are you sure you want to leave?'
      )
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  }
})
</script>

<style></style>
